// R76 — L6's VIOLATION EVIDENCE DRAWER: the flagged exchanges for one member, with the surrounding
// thread so the owner judges fairly, and the three buttons she rules with.
//
// **IT CAME OUT BECAUSE R65's ROSTER COULD NOT CONTAIN IT (R69's lesson: a roster that cannot contain
// the thing it is a roster of reports "all declared" forever).** R65 declared its biconditional over
// the SEVEN `src/components/Owner*.tsx` tabs and closed exactly this shape on five of them — a read
// with no `else`, an act with an empty `.catch()`. This drawer is the EIGHTH owner surface, it lives
// inside the Members tab in App.tsx, and it had both halves:
//
//   - **THE READ LEFT THE DRAWER COMPLETELY BLANK.** `evidence[userId]` stays UNDEFINED on a refusal,
//     so under the button she just tapped — *Review the evidence* — there are no rows, no "Loading
//     the exchanges…", and not even the honest "No preserved exchanges yet for this member."
//     R58's absent section, on the owner's own moderation surface.
//   - **AND A REFUSED RULING SAID NOTHING AT ALL** (R66's stubborn row): the row stays exactly where
//     it is, still marked unreviewed, and she rules on it again.
//
// **FOUR STATES, AND ONLY THREE OF THEM COULD BE DRAWN BEFORE.** Out here a gate CALLS it once per
// state, which a forty-line block nested three deep inside a thirty-hook page never could be.
//
// **TWO NOTES, NOT ONE, AND THE ROUND WOULD HAVE BROKEN ITS OWN RULE WITHOUT IT (R66):** a ruling
// RE-READS the evidence when it lands, so a single shared line would be overwritten by the read's
// silence in the same breath — her act's own account of what the server did, gone, on a surface whose
// consequence is an append-only ledger.
import type { ReactNode } from "react";

export type EvidenceMsg = { role: string; content: string; at: string; flagged: boolean };
export type EvidenceEvent = {
  id: string; gate: string; topic: string; strike: number | null;
  memberMessage: string; lalaResponse: string; ruling: string; rulingNote: string;
  at: string; threadDeleted: boolean; context: EvidenceMsg[];
};
export type EvidenceRuling = "confirmed" | "innocent" | "watch";

export function OwnerEvidenceDrawer({ events, loading, loadNote, actNote, onRule, shortDate }: {
  // `undefined` is "not read yet", `[]` is "genuinely none" — W5-2/R39's third state, and the whole
  // reason a failed read used to be invisible here (R18).
  events: EvidenceEvent[] | undefined;
  loading: boolean;
  loadNote: string;
  actNote: string;
  onRule: (id: string, ruling: EvidenceRuling) => void;
  shortDate: (at: string) => ReactNode;
}) {
  return (
    <div className="owner-probe__evidence">
      {loading && !events ? <p className="owner-page__note">Loading the exchanges...</p> : null}
      {(events || []).map((ev) => (
        <div className="owner-evidence" key={ev.id}>
          <p className="owner-page__note">
            {ev.topic || "protected"}{ev.strike ? ` · strike ${ev.strike}` : ""} · {shortDate(ev.at)}
            {ev.ruling ? ` · ruled ${ev.ruling}` : " · unreviewed"}
            {ev.threadDeleted ? " · thread deleted by member (copy preserved)" : ""}
          </p>
          <div className="owner-evidence__thread">
            {(ev.context.length ? ev.context : [
              { role: "user", content: ev.memberMessage, at: ev.at, flagged: true },
              { role: "assistant", content: ev.lalaResponse, at: ev.at, flagged: false },
            ]).map((m, i) => (
              <p key={i} className={`owner-evidence__msg owner-evidence__msg--${m.role}${m.flagged ? " is-flagged" : ""}`}>
                <span className="owner-evidence__who">{m.role === "user" ? "Member" : "Lala"}:</span> {m.content}
              </p>
            ))}
          </div>
          <div className="owner-evidence__rule">
            <button type="button" className="owner-btn" onClick={() => onRule(ev.id, "confirmed")}>Confirmed</button>
            <button type="button" className="owner-btn" onClick={() => onRule(ev.id, "innocent")}>Innocent</button>
            <button type="button" className="owner-btn" onClick={() => onRule(ev.id, "watch")}>Watch</button>
          </div>
        </div>
      ))}
      {events && !events.length ? <p className="owner-page__note">No preserved exchanges yet for this member.</p> : null}
      {/* Where the exchanges would have been, and where the row she just ruled on is. */}
      {loadNote ? <p className="owner-act-note" role="status">{loadNote}</p> : null}
      {actNote ? <p className="owner-act-note" role="status">{actNote}</p> : null}
    </div>
  );
}
